import axios from "axios";
import { useEffect, useState } from "react"
import { Users, Save, Eye, Lock, Undo2, Edit, MapPin, Briefcase, Trash2, ArrowLeft, Contact, UserCircle, Link, SquaresUnite, AtSign, AlertTriangle, X, CheckCircle2, UserX, EyeOff } from 'lucide-react';
import { Address, Alumni, AlumniExtended, Business, getVisibility, HIDDEN_DATE, HIDDEN_STRING, PrivacyField, PrivacyVisibility, Roles, setVisibility, WebUser } from "iasma-datatypes";
import { useAuth } from "@/contexts/AuthContext"
import { updateAlumni, getModifiedFields } from "@/services/DirectoryService";
import { useNotify } from "@/contexts/NotifyContext";
import { GenderName, getLocalTime, INDONESIA, IndonesiaData, JobCategories, JobSpecialties, ReligionName } from "../../../constants";
import { SectionCollapsible } from "./SectionCollapsible";
import { PrivacySelector } from "./PrivacySelector";
import { ItemNumber } from "./ItemNumber";
import { NavLink } from "react-router-dom";
import { SELF_ENTRY_INDEX_THRESHOLD } from "iasma-datatypes/build/constants-sla";
import { ViewMergeCandidate } from "../ViewMergeCandidate";


export function Profile({ profile, editable, backpath, onUpdate, onMerge, onClose }: {
	profile: AlumniExtended
	editable?: boolean
	backpath?: string
	onUpdate?: (profile: AlumniExtended) => void;
	onMerge?: (original: AlumniExtended, tobeKeep: AlumniExtended, tobeDelete: AlumniExtended) => void;
	onClose?: () => void;
}) {

	const { session, setToken } = useAuth()
	const { setNotify } = useNotify();

	const [userProfile, setUserProfile] = useState<AlumniExtended | undefined>(profile);
	const [tempProfile, setTempProfile] = useState<AlumniExtended | undefined>(userProfile);
	const [isEditing, setIsEditing] = useState(false);
	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [showMerge, setShowMerge] = useState(false);
	const [confirmWebuser, setConfirmWebuser] = useState<WebUser | null>(null);
	const [confirmAlumni, setConfirmAlumni] = useState<Alumni | null>(null);
	const [toast, setToast] = useState<React.ReactNode | null>(null);

	useEffect(() => {
		const checkAdmin = async () => {
			console.log("Profile", profile)
			if (session!.Roles.indexOf(Roles.ADMIN_ALL) >= 0) {
				setIsAdmin(true);
			} else if (session!.Roles.indexOf(Roles.ADMIN_ANGKATAN) >= 0) {
				if (session!.User!.Alumni!.Graduation === profile?.Graduation) {
					setIsAdmin(true);
				}
			}
		}
		if (profile)
			checkAdmin();
	}, [profile]);

	const classLabel = "text-[11px] font-mono uppercase-none text-gray-500";
	const classValue = "px-1 text-[14px] text-gray-900";
	const classValueHidden = "px-1 text-[13px] text-gray-400 italic";
	const classSubValue = "px-1 text-[11px] text-gray-700";
	const classInput = "px-2 py-1.5 bg-white w-full border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";

	const handleEditProfile = () => {
		setTempProfile(userProfile);
		setIsEditing(true);
	};

	const handleSaveProfile = async () => {
		setNotify(null);
		try {
			let modified: any = getModifiedFields<AlumniExtended>(userProfile as AlumniExtended, tempProfile as AlumniExtended);
			let data: any = {
				...modified,
				ExtraData: {
					...tempProfile?.ExtraData,
					History: {
						...(tempProfile?.ExtraData?.History ?? {}),
						[getLocalTime()]: {
							Action: "Data Update",
							By: session?.User?.Alumni?.Name || session?.User?.Email,
							Data: modified
						}
					}
				}
			}

			setToken(axios);

			let result = await updateAlumni(userProfile?.Id!, data);
			setUserProfile(tempProfile);
			if (onUpdate && tempProfile) {
				onUpdate(tempProfile)
			}
			setIsEditing(false);
		} catch (error: any) {
			setNotify({ title: "Update Profil Gagal", error, severity: 'error' });
		}
	};

	const handleMergeProfile = () => {
		if (profile && profile.Id > SELF_ENTRY_INDEX_THRESHOLD) {
			setShowMerge(true)
		}
	}
	const handleMergeExecute = (original: AlumniExtended, tobeKeep: AlumniExtended, tobeDeleted: AlumniExtended) => {
		setShowMerge(false);
		if (onMerge) {
			onMerge(original, tobeKeep, tobeDeleted)
		}
	}

	const handleCancelEdit = () => {
		setIsEditing(false);
	};

	const handleInputChange = (field: any, value: any) => {
		setTempProfile({ ...tempProfile, [field]: value } as AlumniExtended);
	};

	const getPrivacyIcon = (visibility: PrivacyVisibility) => {
		switch (visibility) {
			case PrivacyVisibility.Public:
				return <div className="flex flex-row gap-0.5 text-xs text-green-500 font-semibold font-mono"><Eye size={14} />All Alumni</div>
			case PrivacyVisibility.Angkatan:
				return <div className="flex flex-row gap-0.5 text-xs text-yellow-400 font-semibold font-mono"><Users size={14} />{tempProfile?.Graduation || ''}</div>;
			case PrivacyVisibility.Admin:
				return <div className="flex flex-row gap-0.5 text-xs text-red-400 font-semibold font-mono"><Lock size={14} />Admin</div>;
			default:
				return null;
		}
	};

	function onSetVisibility(privacy: PrivacyField, visibility: PrivacyVisibility) {
		let privacies = setVisibility(tempProfile?.Privacies || '', privacy, visibility);
		setTempProfile({
			...tempProfile,
			Privacies: privacies
		} as AlumniExtended);
	}

	const doConfirmDeleteAlumni = (alumni: AlumniExtended) => {
		if (alumni && alumni.Id > SELF_ENTRY_INDEX_THRESHOLD) {
			setConfirmAlumni(alumni)
		}
	};

	const doDeleteAlumni = () => {
		if (!confirmAlumni) return;
		if (onClose) {
			onClose();
		}
		setConfirmAlumni(null);
	}

	const doConfirmDeleteWebuser = (webuser: WebUser) => {
		setConfirmWebuser(webuser);
	};

	const doDeleteWebuser = () => {
		if (!confirmWebuser) return;
		setTempProfile(prev => ({
			...prev,
			WebUsers: prev?.WebUsers.filter(u => u.Id !== confirmWebuser.Id)
		} as AlumniExtended));

		setToast(`Removed ${confirmWebuser.Email}`);
		setConfirmWebuser(null);
		window.setTimeout(() => setToast(null), 3000);
	}
	
	function HiddenData()  {
		return<EyeOff className="w-4 h-4 mx-2 text-slate-500" />
	}

	function RenderBasic(): React.ReactNode {
		const classBorderInput = isEditing ? "" : " border border-gray-200 rounded p-1";

		return <SectionCollapsible Icon={UserCircle} title="Informasi Umum" isEditing={isEditing} isExpanded={true}>
			<div className="grid grid-cols-6 gap-2">
				{/* Nama (on edit only) */}
				{isEditing && (
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Nama Lahir</label>
						<input type="text"
							value={tempProfile?.Name}
							onChange={(e) => handleInputChange("Name", e.target.value)}
							className={classInput}
						/>
					</div>
				)}

				{/* Jenis Kelamin */}
				<div className={"col-span-3 md:col-span-2" + classBorderInput}>
					<label className={classLabel}>Jenis Kelamin</label>
					{isEditing ? (
						<select value={tempProfile?.Gender}
							onChange={(e) => handleInputChange("Gender", e.target.value)}
							className={classInput}>
							<option value="" disabled>Jenis Kelamin</option>
							<option value="L">Laki-laki</option>
							<option value="P">Perempuan</option>
						</select>
					) : (
						<p className={classValue}>{GenderName(userProfile?.Gender)}</p>
					)}
				</div>

				{/* Agama */}
				<div className={"col-span-3 md:col-span-2" + classBorderInput}>
					<label className={classLabel}>Agama</label>
					{isEditing ? (
						<select value={tempProfile?.Religion}
							onChange={(e) => handleInputChange("Religion", e.target.value)}
							className={classInput}>
							<option value="" disabled>Agama</option>
							<option value="I">{ReligionName("I")}</option>
							<option value="K">{ReligionName("K")}</option>
							<option value="H">{ReligionName("H")}</option>
							<option value="B">{ReligionName("B")}</option>
							<option value="O">{ReligionName("O")}</option>
						</select>
					) : (
						<p className={classValue}>{ReligionName(userProfile?.Religion)}</p>
					)}
				</div>

				{/* Panggilan */}
				<div className={"col-span-3 md:col-span-2" + classBorderInput}>
					<label className={classLabel}>Panggilan</label>
					{isEditing ? (
						<input type="text"
							value={tempProfile?.Alias}
							onChange={(e) => handleInputChange("Alias", e.target.value)}
							className={classInput}
							placeholder="nama kecil"
						/>
					) : (
						<p className={classValue}>{userProfile?.Alias && userProfile?.Alias.length > 0 ? userProfile?.Alias : "-" }</p>
					)}
				</div>

				{/* Titel/Gelar */}
				<div className={"col-span-3" + classBorderInput}>
					<label className={classLabel}>Gelar/Titel</label>
					{isEditing ? (
						<input type="text"
							value={tempProfile?.Title}
							onChange={(e) => handleInputChange("Title", e.target.value)}
							className={classInput}
							placeholder="gelar adat, titel akademis"
						/>
					) : (
						<p className={classValue}>{userProfile?.Title && userProfile?.Title.length > 0 ? userProfile?.Title : "-" }</p>
					)}
				</div>

				{/* Kelas */}
				<div className={"col-span-3" + classBorderInput}>
					<label className={classLabel}>Kelas</label>
					{isEditing ? (
						<input type="text"
							value={tempProfile?.Classes}
							onChange={(e) => handleInputChange("Classes", e.target.value)}
							className={classInput}
							placeholder="nama kelas 1 s/d kelas 3"
						/>
					) : (
						<p className={classValue}>{userProfile?.Classes && userProfile?.Classes.length > 0 ? userProfile?.Classes : "-" }</p>
					)}
				</div>

				{/* Tanggal Lahir */}
				<div className={"col-span-3" + classBorderInput}>
					<label className={classLabel}>Tanggal Lahir</label>
					{isEditing ? (
						<div className="w-full flex flex-row gap-1">
							<input type="date"
								value={tempProfile?.BirthDate  ? tempProfile.BirthDate.split('T')[0] : tempProfile?.BirthDate || ''}
								onChange={(e) => handleInputChange("BirthDate", e.target.value)}
								className={classInput}
							/>
							<PrivacySelector
								isEditing={isEditing}
								visibility={getVisibility(tempProfile?.Privacies || '', PrivacyField.Birthday)}
								onChange={(e) => onSetVisibility(PrivacyField.Birthday, e as PrivacyVisibility)}
							/>
						</div>
					) : (
						<div className="flex flex-row gap-1 items-center">
							{userProfile?.BirthDate === HIDDEN_DATE ?
								<HiddenData />
								:
								userProfile?.BirthDate ? userProfile.BirthDate.split('T')[0] : "-"
							}
							{ userProfile?.BirthDate && userProfile?.BirthDate.length > 0 && 
								getPrivacyIcon(getVisibility(userProfile?.Privacies || '', PrivacyField.Birthday))
							}
						</div>
					)}
				</div>

				{/* Nomor HP */}
				<div className={"col-span-3" + classBorderInput}>
					<label className={classLabel}>Nomor HP</label>
					{isEditing ? (
						<div className="flex flex-row gap-1">
							<input type="text"
								value={tempProfile?.Phones}
								onChange={(e) => handleInputChange("Phones", e.target.value)}
								className={classInput}
								placeholder="nomor handphone yg dapat dihubungi"
							/>
							<PrivacySelector
								isEditing={isEditing}
								visibility={getVisibility(tempProfile?.Privacies || '', PrivacyField.Phone)}
								onChange={(e) => onSetVisibility(PrivacyField.Phone, e as PrivacyVisibility)}
							/>
						</div>

					) : (
						<div className="flex flex-row gap-1 items-center">
							{userProfile?.Phones === HIDDEN_STRING ?
								<HiddenData />
								:
								userProfile?.Phones ? userProfile?.Phones : "-"
							}
							{userProfile?.Phones && userProfile?.Phones.length > 0 && 
								getPrivacyIcon(getVisibility(userProfile?.Privacies || '', PrivacyField.Phone))
							}
						</div>
					)}
				</div>

				{/* Data Kematian */}
				{ (tempProfile?.Id !== session?.User?.Alumni?.Id) && (
					<div className="col-span-3">
						{isEditing ? (
							<div className={classBorderInput}>
								<label className={classLabel}>Data Kematian</label>
								<input type="text"
									value={tempProfile?.DOD}
									onChange={(e) => handleInputChange("DOD", e.target.value)}
									className={classInput}
									placeholder="info/tanggal kematian"
								/>
							</div>
						) 
					: 
						tempProfile?.DOD 
						? 
							<div className={classBorderInput}>
								<label className={classLabel}>Data Kematian</label>
								<p className={classValue}>{userProfile?.DOD}</p> 
							</div>
						: 
							<div></div>
						}
					</div>
				)}

				<div>
					{/* if admin, able to mark or field as death */}
				</div>
			</div>
		</SectionCollapsible>
	}

	function RenderAddress(): React.ReactNode {
		const addAddress = () => {
			if (!tempProfile) return;
			const newAddress: Address = {
				Id: 1 + (tempProfile.Addresses?.length || 0),
				Kind: 'rumah',
				Street: '',
				Village: '',
				District: '',
				City: '',
				State: '',
				ZipCode: '',
				Country: INDONESIA,
			};
			setTempProfile(prev => ({ ...prev, Addresses: [...(prev?.Addresses || []), newAddress] } as AlumniExtended));
		};

		const updateAddress = (Id: number, field: keyof Address, value: string) => {
			setTempProfile(prev => ({
				...prev,
				Addresses: prev?.Addresses.map(addr => {
					if (addr.Id === Id) {
						const updatedAddr = { ...addr, [field]: value };
						if (field === 'State') {
							updatedAddr.City = '';
						}
						return updatedAddr;
					}
					return addr;
				})
			} as AlumniExtended));
		};

		const deleteAddress = (Id: number) => {
			setTempProfile(prev => ({
				...prev,
				Addresses: prev?.Addresses.filter(addr => addr.Id !== Id)
			} as AlumniExtended));
		};

		const getAddressTypeLabel = (type: string) => {
			const labels: Record<string, string> = {
				rumah: 'Rumah',
				kampung: 'Kampung',
				lain: 'Lainnya'
			};
			return labels[type] || type;
		};

		if (isEditing && tempProfile) {
			return <SectionCollapsible Icon={MapPin} title="Alamat" count={tempProfile.Addresses.length} isEditing={isEditing} onAdd={addAddress}>
				{tempProfile.Addresses.map((address, index) => (
					<div className="grid grid-cols-2 gap-2 border border-gray-100 shadow-sm rounded-sm p-1">
						{/* Address.Kind */}
						<div className="col-span-2 flex justify-between items-start gap-1">
							<div className="flex flex-row gap-1 items-center">
								<ItemNumber value={index + 1} />
								<select
									value={address.Kind}
									onChange={(e) => updateAddress(address.Id, 'Kind', e.target.value)}
									className={classInput} >
									<option value="rumah">Rumah</option>
									<option value="kampung">Kampung</option>
									<option value="lain">Lainnya</option>
								</select>
							</div>
							<button
								onClick={() => deleteAddress(address.Id)}
								className="text-red-600 hover:text-red-700 p-1">
								<Trash2 className="w-4 h-4" />
							</button>
						</div>

						{/* Address.Country */}
						<div className="relative">
							<input
								type="text"
								value={address.Country}
								onChange={(e) => updateAddress(address.Id, 'Country', e.target.value)}
								placeholder="Negara"
								list={`country-list-${address.Id}`}
								className={classInput}
							/>
							<datalist id={`country-list-${address.Id}`}>
								{[INDONESIA, 'Luar Negri']?.map((spec) => (
									<option key={spec} value={spec} />
								))}
							</datalist>
						</div>

						{/* Address.ZipCode */}
						<input type="text"
							value={address.ZipCode}
							onChange={(e) => updateAddress(address.Id, 'ZipCode', e.target.value)}
							className={classInput}
							placeholder="Kode POS"
						/>

						{/* Provinsi */}
						{address.Country === INDONESIA ? (
							<select
								value={address.State}
								onChange={(e) => updateAddress(address.Id, 'State', e.target.value)}
								className={classInput}
							>
								<option value="">Pilih Provinsi</option>
								{Object.keys(IndonesiaData).map((province) => (
									<option key={province} value={province}>
										{province}
									</option>
								))}
							</select>
						) : (
							<input
								type="text"
								value={address.State}
								onChange={(e) => updateAddress(address.Id, 'State', e.target.value)}
								className="bg-white px-2 py-1 border border-gray-300 rounded-md"
								placeholder="Provinsi"
							/>
						)}

						{/* Kota/Kabupaten */}
						{address.Country === INDONESIA ? (
							<select
								value={address.City}
								onChange={(e) => updateAddress(address.Id, 'City', e.target.value)}
								className={classInput}
								disabled={!address.State}
							>
								<option value="">Pilih Kota/Kabupaten</option>
								{address.State && IndonesiaData[address.State]?.map((city) => (
									<option key={city} value={city}>
										{city}
									</option>
								))}
							</select>
						) : (
							<input
								type="text"
								value={address.City}
								onChange={(e) => updateAddress(address.Id, 'City', e.target.value)}
								className="bg-white px-2 py-1 border border-gray-300 rounded-md"
								placeholder="Kota/Kabupaten"
							/>
						)}

						{/* Kecamatan */}
						<input
							type="text"
							value={address.District}
							onChange={(e) => updateAddress(address.Id, 'District', e.target.value)}
							className={classInput}
							placeholder="Kecamatan"
						/>

						{/* Kelurahan */}
						<input
							type="text"
							value={address.Village}
							onChange={(e) => updateAddress(address.Id, 'Village', e.target.value)}
							className={classInput}
							placeholder="Kelurahan"
						/>

						{/* Jalan, Nomor, RT, RW, .. */}
						<div className="col-span-2 flex gap-1 mb-4">
							<input
								type="text"
								value={address.Street}
								onChange={(e) => updateAddress(address.Id, 'Street', e.target.value)}
								className={classInput}
								placeholder="Nama Jalan"
							/>
							<PrivacySelector
								isEditing={isEditing}
								visibility={getVisibility(tempProfile?.Privacies || '', PrivacyField.Address)}
								onChange={(e) => onSetVisibility(PrivacyField.Address, e as PrivacyVisibility)}
							/>
						</div>
					</div>
				))}
			</SectionCollapsible>
		} else if (userProfile) {
			return <SectionCollapsible Icon={MapPin} title="Alamat" count={userProfile.Addresses.length} isEditing={isEditing}>
				{userProfile.Addresses.map((address, index) => (
					<div className="mb-2">
						<div className="flex items-center gap-1">
							<ItemNumber value={index + 1} />
							<span className={classValue + "inline-flex items-center rounded-md px-2 py-1  font-medium  inset-ring inset-ring-green-600/20"}>
								{getAddressTypeLabel(address.Kind)}
							</span>
						</div>
						<div className="flex">
							{address.Street === HIDDEN_STRING ?
								<HiddenData />
								:
								<p className={classValue}>{address.Street}</p>
							}
							{getPrivacyIcon(getVisibility(userProfile?.Privacies || '', PrivacyField.Birthday))}
						</div>
						<p className={classSubValue}>{address.Village}, {address.District}, {address.City}</p>
						<p className={classSubValue}>{address.State} {address.ZipCode}</p>
					</div>
				))}
			</SectionCollapsible>
		}
	}

	function RenderBusiness(): React.ReactNode {
		const addBusiness = () => {
			if (!tempProfile) return;
			const newBusiness: Business = {
				Id: 1 + (tempProfile?.Businesses?.length || 0),
				Title: '',
				Company: '',
				Category: '',
				Specialty: '',
				Periode: '',
				IsActive: true,
				Description: ''
			};
			setTempProfile(prev => ({ ...prev, Businesses: [...(prev?.Businesses || []), newBusiness] } as AlumniExtended));
		};

		const updateBusiness = (Id: number, field: keyof Business, value: string | boolean) => {
			setTempProfile(prev => ({
				...prev,
				Businesses: prev?.Businesses.map(business => {
					if (business.Id === Id) {
						const updateBns = { ...business, [field]: value };
						if (field === 'Category') {
							updateBns.Specialty = '';
						}
						return updateBns;
					}
					return business;
				})
			} as AlumniExtended));
		};

		const deleteBusiness = (Id: number) => {
			setTempProfile(prev => ({
				...prev,
				Businesses: prev?.Businesses.filter(bns => bns.Id !== Id)
			} as AlumniExtended));
		};

		const classBorderInput = isEditing ? "" : " border border-gray-200 rounded-sm p-1";

		return <SectionCollapsible Icon={Briefcase} title="Karir/Pekerjaan" count={tempProfile?.Businesses.length} isEditing={isEditing} onAdd={addBusiness}>
			{tempProfile?.Businesses.map((business, index) => (
				<div className="grid grid-cols-6 gap-2 border border-gray-100 shadow-sm rounded-sm p-1">
					{/* Business.IsActive */}
					<div className="col-span-6 flex justify-between items-start gap-1">
						{isEditing ? (
							<>
								<div className="flex flex-row gap-1 items-center">
									<ItemNumber value={index + 1} />
									<label className="flex items-center gap-2">
										<input
											type="checkbox"
											checked={business.IsActive}
											onChange={(e) => updateBusiness(business.Id, 'IsActive', e.target.checked)}
											className={classInput} />
										<span className={business.IsActive ? 'text-green-600' : 'text-gray-700'}>
											Aktif
										</span>
									</label>
								</div>
								<button
									onClick={() => deleteBusiness(business.Id)}
									className="text-red-600 hover:text-red-700 p-1">
									<Trash2 className="w-4 h-4" />
								</button>
							</>
						) : (
							<div className="flex flex-row gap-1 items-center">
								<ItemNumber value={index + 1} />
								<p className={classValue}>{business.IsActive ? 'Masih Aktif' : 'Tidak Aktif'}</p>
							</div>
						)}
					</div>

					{/* Business.Title */}
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Jabatan</label>
						{isEditing ? (
							<input
								type="text"
								value={business.Title}
								onChange={(e) => updateBusiness(business.Id, 'Title', e.target.value)}
								className={classInput}
								placeholder="Nama Jabatan"
							/>
						) : (
							<p className={classValue}>{business.Title}</p>
						)}
					</div>

					{/* Business.Company */}
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Perusahaan</label>
						{isEditing ? (
							<input
								type="text"
								value={business.Company}
								onChange={(e) => updateBusiness(business.Id, 'Company', e.target.value)}
								className={classInput}
								placeholder="Nama Perusahaan"
							/>
						) : (
							<p className={classValue}>{business.Company}</p>
						)}
					</div>

					{/* Business.Periode */}
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Periode Aktif</label>
						{isEditing ? (
							<input
								type="text"
								value={business.Periode}
								onChange={(e) => updateBusiness(business.Id, 'Periode', e.target.value)}
								className={classInput}
								placeholder="Periode tahun awal-akhir"
							/>
						) : (
							<p className={classValue}>{business.Periode}</p>
						)}
					</div>

					{/* Business.Category */}
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Bidang/Kategori</label>
						{isEditing ? (
							<select
								value={business.Category}
								onChange={(e) => updateBusiness(business.Id, 'Category', e.target.value)}
								className={classInput}
							>
								<option key="">Bidang/Kategori</option>
								{JobCategories.map((cat) => (
									<option key={cat} value={cat}>
										{cat}
									</option>
								))}
							</select>
						) : (
							<p className={classValue}>{business.Category}</p>
						)}
					</div>

					{/* Business.Specialty */}
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Profesi/Pekerjaan</label>
						{isEditing ? (<>
							<input
								type="text"
								value={business.Specialty}
								onChange={(e) => updateBusiness(business.Id, 'Specialty', e.target.value)}
								list={`specialty-list-${business.Id}`}
								className={classInput}
								placeholder="profesi/pekerjaan/spesilisasi"
							/>
							<datalist id={`specialty-list-${business.Id}`}>
								{JobSpecialties[business.Category]?.map((spec) => (
									<option key={spec} value={spec} />
								))}
							</datalist>
						</>
						) : (
							<p className={classValue}>{business.Specialty}</p>
						)}
					</div>

					{/* Business.Description */}
					<div className={"col-span-6" + classBorderInput}>
						<label className={classLabel}>Keterangan</label>
						{isEditing ? (
							<textarea
								value={business.Description}
								onChange={(e) => updateBusiness(business.Id, 'Description', e.target.value)}
								className={classInput}
								rows={3}
								placeholder="Tambahan informasi keahlian/pekerjaan..."
							/>
						) : (
							<p className={classValue}>{business.Description}</p>
						)}
					</div>
				</div>
			))}
		</SectionCollapsible>
	}

	function RenderFamilies(): React.ReactNode {
		if (userProfile) {
			return <SectionCollapsible Icon={Contact} title="Kerabat" count={userProfile.Relations.length} isEditing={isEditing}>
				{userProfile.Relations.length > 0 ?
					userProfile.Relations.map((relation, index) => (
						<div className="mb-6">
							<div className="flex items-center gap-2 mb-3">
								<span className="py-1 text-emerald-700 text-md font-medium rounded-full">
									<span className="inline-flex items-center justify-center w-6 h-6  font-bold text-white  bg-emerald-600 rounded-full mr-1">
										{index + 1}
									</span>
									<span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1  font-medium text-emerald-700 inset-ring inset-ring-emerald-600/20">
										{relation.Alumni_A.Name}
									</span>
									{" adalah "}
									<span className="px-3 py-1 bg-green-100 text-green-700  font-medium rounded-full">
										{relation.Kind}
									</span>
									{" dari "}
									<span className="px-3 py-1 bg-green-100 text-green-700  font-medium rounded-full">
										{relation.Alumni_B.Name + " (" + relation.Alumni_B.Graduation + ")"}
									</span>
								</span>
							</div>
						</div>
					)
					) : (
						<div className="mt-6 p-4 rounded-lg shadow-md bg-(--color-surface)">
							<strong>belum implementasi.</strong> menambahkan data kekerabatan antara alumni dengan mencari profil alumni dimenu Direktori alumni, dari profil alumni tersebut dapat dihubungan data kekerabatan.
							kekerabatan seperti: ayah, ibu, anak, saudara, sepupu, paman, tante, kakek, nenek, cucu, dll
						</div>
					)}
			</SectionCollapsible>

		}
	}

	function RenderExternalLinks(): React.ReactNode {
		return <SectionCollapsible Icon={Link} title="Tautan Luar" count={0} isEditing={isEditing}>
			<div className="mt-6 p-4 rounded-lg shadow-md bg-(--color-surface)">
				<strong>belum implementasi.</strong> berisikan fitur menambahkan link external terkait profil, seperti: instagram, facebook, linkedin, dll
			</div>
		</SectionCollapsible>
	}

	function RenderEmails(): React.ReactNode {
		let elements: React.ReactNode[] = []
		if (!tempProfile || !tempProfile.WebUsers || tempProfile.WebUsers.length === 0) {
			elements.push(<div className="text-sm text-gray-500">Tidak ada login email</div>);
		} else {
			for (const webuser of tempProfile.WebUsers) {
				elements.push(<div key={webuser.Id} className="relative">
					<div className="flex flex-row justify-between items-center gap-1">
						<span className="font-semibold">{webuser.Name}</span>
						{isEditing && (session?.User?.Email === webuser.Email
							? <span className="text-xs text-gray-400">(login akun)</span>
							: <button
								onClick={() => doConfirmDeleteWebuser(webuser)}
								className="text-red-600 hover:text-red-700 p-1">
								<Trash2 className="w-4 h-4" />
							</button>
						)
						}

					</div>

					<div className="text-sm font-medium text-slate-500">
						&lt;{webuser.Email}&gt;
					</div>
				</div>);
			}
		}
		return <SectionCollapsible Icon={AtSign} title="Email Logins" count={profile.WebUsers?.length | 0} isEditing={false}>
			<div className="">
				<div className="space-y-4 p-5 bg-blue-50/20" >
					<div className="relative space-y-5 border-l border-slate-200 pl-5">
						{elements}
					</div>
				</div>
			</div>
		</SectionCollapsible>
	}

	function stringifyNoQuotesPretty(obj: any) {
		return Object.entries(obj)
			.map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
			.join('\n');
	}

	function showHistoryValue(key: string, value: any): React.ReactNode {
		let newValue: React.ReactNode = String(value);
		let isFlexRow = true;
		if (value && typeof value === "object") {
			try {
				newValue = <pre>{stringifyNoQuotesPretty(value)}</pre>;
				isFlexRow = false;
			} catch (error) {
			}
		}
		if (value && value.XD && typeof value.XD === "object") {
			try {
				newValue = <pre>{stringifyNoQuotesPretty(value.XD)}</pre>;
				isFlexRow = false;
			} catch (error) {
			}
		}
		return <div className={`mt-1 text-sm flex ${isFlexRow ? 'flex-row' : 'flex-col'} gap-0.5`} key={key}>
			<div className="text-slate-400">
				{key}:
			</div>
			<div className={`text-slate-800 font-semibold wrap-anywhere ${isFlexRow ? 'ml-1' : 'ml-5'}`}>
				{newValue}
			</div>
		</div>
	}

	function RenderExtraDataHistory(xd: any): React.ReactNode {
		let elements: React.ReactNode[] = []
		let count = 0
		if (!xd || !xd.History) {
			elements.push(<div className="text-sm text-gray-500">Tidak ada riwayat</div>);
		} else {
			let idx = 0;
			let keys = Object.keys(xd.History).sort((a, b) => b.localeCompare(a));
			count = keys.length;
			for (const key of keys) {
				const item = xd.History[key];
				elements.push(<div key={idx} className="relative">
					<div className="absolute -left-6.25 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-slate-400 ring-1 ring-blue-600" />
					<div className="text-sm font-semibold text-blue-600">
						{key}
					</div>
					{Object.entries(item).map(([keyx, valuex]) => (
						showHistoryValue(keyx, valuex)
					))}
				</div>);
				idx++;
			}
		}

		return <SectionCollapsible Icon={Link} title="Riwayat Perubahan" count={count} isEditing={false}>
			<div className="">
				<div className="space-y-4 p-5 bg-blue-50/20" >
					<div className="relative space-y-5 border-l border-slate-200 pl-5">
						{elements}
					</div>
				</div>
			</div>
		</SectionCollapsible>
	}

	// Render Profile View
	if (showMerge)
		return <ViewMergeCandidate selfEntry={profile} onCancel={() => setShowMerge(false)} onMerge={handleMergeExecute} />
	else
		return (
			<div className="p-1 max-w-150 bg-(--color-background) text-(--color-text)">
				<div className="mb-3 flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div className="flex min-w-0 flex-1 flex-col items-start justify-between text-left">
						<h3 className="text-xl font-bold text-left">
							{tempProfile?.Name}
						</h3>
						<p className="text-sm text-left">Angkatan {tempProfile?.Graduation} {tempProfile?.NIS ? ` (NIS: ${tempProfile?.NIS})` : ''}</p>
					</div>
					<div className="ml-auto mt-1 flex justify-end">
						{!isEditing ? (
							<div className="flex gap-1 flex-wrap">
								{onClose ?
									<button className="bg-blue-100 text-blue-600 px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={onClose}>
										<ArrowLeft size={16} />
										<span>Balik</span>
									</button>
									: backpath 
										? <button className="bg-blue-100 text-blue-600 px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs">
											<NavLink to={backpath} className="inline-flex items-center gap-1.5">
												<ArrowLeft size={16} />
												<span>Balik</span>
											</NavLink>
										</button>
										: null
								}
								{editable &&
									<button className="bg-blue-400 text-white px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={handleEditProfile}>
										<Edit size={16} />
										<span>Ubah</span>
									</button>
								}
								{isAdmin && tempProfile && (tempProfile?.Id > SELF_ENTRY_INDEX_THRESHOLD) &&
									<button className="bg-green-700 text-white px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={handleMergeProfile}>
										<SquaresUnite size={16} />
										<span>Gabung</span>
									</button>
								}
								{isAdmin && tempProfile && (tempProfile?.Id > SELF_ENTRY_INDEX_THRESHOLD) &&
									<button className="bg-red-400 text-white px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={() => doConfirmDeleteAlumni(tempProfile)}>
										<UserX size={16} />
										<span>Hapus</span>
									</button>
								}
							</div>
						) : (
							<div className="flex gap-2 flex-wrap">
								<button className="bg-green-400 text-white px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={handleSaveProfile}>
									<Save size={16} />
									<span>Simpan</span>
								</button>
								<button className="bg-orange-400 text-white px-2 py-1.5 rounded-lg cursor-pointer inline-flex items-center gap-1.5 text-xs" onClick={handleCancelEdit}>
									<Undo2 size={16} />
									<span>Batal</span>
								</button>
							</div>
						)}
					</div>
				</div>

				{RenderBasic()}
				{/* Akademik */}
				{RenderBusiness()}
				{RenderAddress()}
				{/* {RenderFamilies()} */}
				{/* {RenderExternalLinks()} */}
				{isAdmin && RenderEmails()}
				{isAdmin && !isEditing && RenderExtraDataHistory(profile?.ExtraData)}

				{/* Confirmation Delete WebUser */}
				{confirmWebuser && (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center bg-[#14201D]/50 px-4"
						role="dialog"
						aria-modal="true"
						aria-labelledby="delete-email-title"
						onClick={() => setConfirmWebuser(null)}
					>
						<div
							className="w-full max-w-sm bg-white rounded-xl shadow-xl border border-[#E3E1D8] p-6"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="flex items-start justify-between mb-4">
								<div className="h-10 w-10 rounded-full bg-[#8B4A3C]/10 flex items-center justify-center">
									<AlertTriangle className="h-5 w-5 text-[#8B4A3C]" strokeWidth={2} />
								</div>
								<button
									onClick={() => setConfirmWebuser(null)}
									className="text-[#9AA39D] hover:text-[#5B655F] transition-colors"
									aria-label="Close"
								>
									<X className="h-5 w-5" />
								</button>
							</div>

							<h3 id="delete-email-title" className="text-base font-semibold text-[#14201D]">
								Hapus alamat email ini?
							</h3>
							<p className="text-sm text-[#5B655F] mt-2 leading-relaxed">
								<span className="font-medium text-[#14201D]">{confirmWebuser.Email}</span> tidak dapat digunakan untuk login sebagai alumni terkait. Namun email dapat digunakan kembali dengan melakukan registrasi ulang. 
							</p>

							<div className="flex gap-3 mt-6">
								<button
									onClick={() => setConfirmWebuser(null)}
									className="flex-1 px-4 py-2 rounded-md border border-[#E3E1D8] text-sm font-medium text-[#14201D] hover:bg-[#F6F5F1] transition-colors"
								>
									Batal
								</button>
								<button
									onClick={doDeleteWebuser}
									className="flex-1 px-4 py-2 rounded-md bg-[#8B4A3C] text-sm font-medium text-white hover:bg-[#733D31] transition-colors"
								>
									Hapus email
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Confirmation Delete Alumni */}
				{confirmAlumni && (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center bg-[#14201D]/50 px-4"
						role="dialog"
						aria-modal="true"
						aria-labelledby="delete-alumni-title"
						onClick={() => setConfirmAlumni(null)}
					>
						<div
							className="w-full max-w-sm bg-white rounded-xl shadow-xl border border-[#E3E1D8] p-6"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="flex items-start justify-between mb-4">
								<div className="h-10 w-10 rounded-full bg-[#8B4A3C]/10 flex items-center justify-center">
									<AlertTriangle className="h-5 w-5 text-[#8B4A3C]" strokeWidth={2} />
								</div>
								<button
									onClick={() => setConfirmAlumni(null)}
									className="text-[#9AA39D] hover:text-[#5B655F] transition-colors"
									aria-label="Close"
								>
									<X className="h-5 w-5" />
								</button>
							</div>

							<h3 id="delete-alumni-title" className="text-base font-semibold text-[#14201D]">
								Hapus data alumni ini?
							</h3>
							<p className="text-sm text-[#5B655F] mt-2 leading-relaxed">
								<span className="font-medium text-[#14201D]">{confirmAlumni.Name}</span> 
							</p>
							<p className="text-sm text-[#5B655F] mt-2 leading-relaxed">
								Angkatan <span className=" text-[#14201D]">{confirmAlumni.Graduation}</span> 
							</p>

							<div className="flex gap-3 mt-6">
								<button
									onClick={() => setConfirmAlumni(null)}
									className="flex-1 px-4 py-2 rounded-md border border-[#E3E1D8] text-sm font-medium text-[#14201D] hover:bg-[#F6F5F1] transition-colors"
								>
									Batal
								</button>
								<button
									onClick={doDeleteAlumni}
									className="flex-1 px-4 py-2 rounded-md bg-[#8B4A3C] text-sm font-medium text-white hover:bg-[#733D31] transition-colors"
								>
									Hapus alumni
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Toast */}
				{toast && (
					<div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#14201D] text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
						<CheckCircle2 className="h-4 w-4 text-[#7FBF9E]" strokeWidth={2} />
						{toast}
					</div>
				)}
			</div>
		);
}


